import { useState } from 'react';

import MinusIcon from '@/app/assets/svg/minus.svg?react';
import PlusIcon from '@/app/assets/svg/plus.svg?react';
import { cn } from '@/app/shared/lib/cn';

export type FaqItem = {
  title: string;
  content: string[];
};

export interface FaqCategories {
  [key: string]: FaqItem[];
}

export interface AccordionProps {
  items: FaqCategories;
}

export const Accordion = ({ items }: AccordionProps) => {
  const [openId, setOpenId] = useState<string | null>(null);

  const handleToggle = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        if (part === 'https://discord.gg') {
          return (
            <a
              key={i}
              href='https://discord.com/invite/j27ARXEfvV'
              target='_blank'
              rel='noopener noreferrer'
              className='text-brand-400 break-all underline'
            >
              https://discord.gg
            </a>
          );
        }

        return (
          <a
            key={i}
            href={part}
            target='_blank'
            rel='noopener noreferrer'
            className='text-brand-400 break-all underline'
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className='mt-12.5 flex w-full flex-col gap-6 pb-20'>
      {Object.entries(items).map(([category, questions]) => (
        <div key={category} className='flex flex-col'>
          <h2 className='mb-2 px-5 text-lg font-bold tracking-wider text-gray-300 min-[390px]:text-xl'>
            {category}
          </h2>

          <div className='flex flex-col'>
            {questions.map((item, index) => {
              const currentId = `${category}-${index}`;
              const isOpen = openId === currentId;

              return (
                <div
                  key={currentId}
                  className={cn(
                    'flex cursor-pointer flex-col overflow-hidden p-5',
                    isOpen ? 'bg-gray-800' : 'bg-transparent',
                  )}
                  onClick={() => handleToggle(currentId)}
                >
                  <div className='flex w-full shrink-0 items-center justify-between text-left select-none'>
                    <span className={cn('leading-6 font-medium text-gray-200')}>{item.title}</span>
                    <span className='ml-4 text-gray-500'>{isOpen ? <MinusIcon /> : <PlusIcon />}</span>
                  </div>

                  <div
                    className={cn(
                      'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <div className='overflow-hidden'>
                      <div className='pt-6 pr-8 text-base leading-relaxed font-normal text-gray-300'>
                        <ul className='list-disc space-y-3 pl-4.5 marker:text-gray-300'>
                          {item.content.map((text, i) => (
                            <li key={i}>{renderTextWithLinks(text)}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
